import {FormattedMessage, useIntl} from "react-intl";
import {PrepopulationLogicListProps, PrepopulationLogicRuleProps, RecursiveQuestionMappingRuleProps} from "./types";
import Button from "@ui-components/Button";
import {PencilSquareIcon, TrashIcon} from "@heroicons/react/24/outline";
import _ from "lodash";
import {
  QuotationSurveyPrepopulationRuleSchema,
  QuotationSurveyRecursiveCopySettingsSchema
} from "@feature/types";

const PrepopulationLogicRule = ({
  questionOptions,
  service,
  rule,
  index,
  onEdit,
  onDelete
}: PrepopulationLogicRuleProps) => {

  // hooks
  const intl = useIntl();

  // utilities
  const response = rule.value_type === "dynamic"
    ? service?.configurations.find((c) => c.id === rule.value)?.name
    : (questionOptions?.length > 0
      ? (Array.isArray(rule.value)
        ? rule.value
            .map((v) => questionOptions.find((o) => o.id == v)?.name)
            .filter((v) => !!v)
            .join(" + ")
        : questionOptions.find((o) => o.id == rule.value)?.name
      )
      : typeof rule.value === "boolean"
        ? _.capitalize(intl.formatMessage({id: rule.value ? "yes" : "no"}))
        : rule.value
    );

  return (
    <div className="flex flex-col gap-1 p-3 border text-xs">
      <div className="flex flex-row justify-between gap-4 mb-1">
        <p className="font-semibold">
          <FormattedMessage id="logic" defaultMessage="Logica" /> {index+1}
        </p>
        <div className="flex flex-row items-center gap-2">
          <PencilSquareIcon
            className="w-4 cursor-pointer"
            onClick={onEdit}
          />
          <TrashIcon
            className="w-4 cursor-pointer"
            onClick={onDelete}
          />
        </div>
      </div>
      <p>
        <FormattedMessage id="logic_reference" defaultMessage="Riferimento" />:{" "}
        {service?.name || "-"}
      </p>
      <p>
        <FormattedMessage id="logic_value" defaultMessage="Valore" />:{" "}
        <FormattedMessage id={`logic_value_type_${rule.value_type}`} />
      </p>
      <p>
        <FormattedMessage id="response" defaultMessage="Risposta" />:{" "}
        {response || "-"}
      </p>
      <p>
        <FormattedMessage id="conditions" defaultMessage="Condizioni" />:{" "}
        {_.capitalize(intl.formatMessage({id: !!rule.dependencies?.length ? "yes" : "no"}))}
      </p>
    </div>
  )
}

const RecursiveQuestionMappingRule = ({
  service,
  rule,
  index,
  onEdit,
  onDelete
}: RecursiveQuestionMappingRuleProps) => {

  // utilities
  const selectedRecursiveQuestion = service
    ?.configurations
    .find(({id}) => id === rule.id_source_recursive_question);

  return (
    <div className="flex flex-col gap-1 p-3 border text-xs">
      <div className="flex flex-row justify-between gap-4 mb-1">
        <p className="font-semibold">
          <FormattedMessage id="copy_configuration" defaultMessage="Configurazione di copia" /> {index+1}
        </p>
        <div className="flex flex-row items-center gap-2">
          <PencilSquareIcon
            className="w-4 cursor-pointer"
            onClick={onEdit}
          />
          <TrashIcon
            className="w-4 cursor-pointer"
            onClick={onDelete}
          />
        </div>
      </div>
      <p>
        <FormattedMessage id="logic_reference" defaultMessage="Riferimento" />:{" "}
        {service?.name || "-"}
      </p>
      <p>
        <FormattedMessage id="recursive_question" defaultMessage="Domanda ricorsiva" />:{" "}
        {selectedRecursiveQuestion?.name || "-"}
      </p>
    </div>
  )
}

export const PrepopulationLogicRulesEditor = ({
  questionOptions,
  logic,
  onAdd,
  onEdit,
  onDelete,
  designServices,
}: PrepopulationLogicListProps) => {

  // utilities
  const {type} = logic || {};
  const ruleBlocks = (type === "simple"
    ? logic?.rules
    : (type === "recursive_copy"
      ? logic?.recursive_copy_settings
      : undefined
    )
  ) ?? [];

  return <>
    <div className="flex flex-col gap-2">
      {ruleBlocks.map((block, idx) => {
        const commonProps = {
          key: idx,
          index: idx,
          service: designServices.find((s) => s.id === block.id_design_service),
          onEdit: () => { onEdit(idx); },
          onDelete: () => { onDelete(idx); },
        };
        if (type === "simple")
          return (
            <PrepopulationLogicRule
              {...commonProps}
              rule={block as QuotationSurveyPrepopulationRuleSchema}
              questionOptions={questionOptions}
            />
          );
        else if (type === "recursive_copy")
          return (
            <RecursiveQuestionMappingRule
              {...commonProps}
              rule={block as QuotationSurveyRecursiveCopySettingsSchema}
            />
          );
        throw new Error(`Invalid logic type: ${type || "undefined"}`);
      })}
    </div>
    <Button
      styleType="white"
      full={true}
      onClick={onAdd}
    >
      <FormattedMessage
        id="add_logic"
        defaultMessage="Aggiungi logica"
      />
    </Button>
  </>
}
